import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Terminal, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface DebugLog {
  id: string;
  timestamp: string;
  type: 'llm-request' | 'llm-response' | 'error' | 'info';
  message: string;
  data?: any;
  model?: string;
  processingTime?: number;
}

interface LLMDebugPanelProps {
  onLogCapture?: (log: DebugLog) => void;
}

export const LLMDebugPanel: React.FC<LLMDebugPanelProps> = ({ onLogCapture }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (isAutoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isAutoScroll]);

  // Listen to console logs and capture relevant ones
  useEffect(() => {
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    const captureLog = (type: DebugLog['type'], message: string, ...args: any[]) => {
      // Only capture logs related to LLM/planner/search
      const relevantKeywords = ['planner', 'llm', 'groq', 'claude', 'enhanced', 'fomo-planner', 'search'];
      const isRelevant = relevantKeywords.some(keyword => 
        message.toLowerCase().includes(keyword) || 
        args.some(arg => typeof arg === 'string' && arg.toLowerCase().includes(keyword))
      );

      if (isRelevant) {
        const log: DebugLog = {
          id: Date.now().toString() + Math.random(),
          timestamp: new Date().toLocaleTimeString(),
          type,
          message,
          data: args.length > 0 ? args : undefined
        };

        setLogs(prev => [...prev.slice(-49), log]); // Keep last 50 logs
        onLogCapture?.(log);
      }
    };

    console.log = (message: any, ...args: any[]) => {
      originalConsoleLog(message, ...args);
      captureLog('info', String(message), ...args);
    };

    console.error = (message: any, ...args: any[]) => {
      originalConsoleError(message, ...args);
      captureLog('error', String(message), ...args);
    };

    console.warn = (message: any, ...args: any[]) => {
      originalConsoleWarn(message, ...args);
      captureLog('error', String(message), ...args);
    };

    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, [onLogCapture]);

  const clearLogs = () => {
    setLogs([]);
  };

  const copyLogs = () => {
    const logText = logs.map(log => 
      `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}${log.data ? '\n' + JSON.stringify(log.data, null, 2) : ''}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(logText);
  };

  const getTypeColor = (type: DebugLog['type']) => {
    switch (type) {
      case 'llm-request': return 'bg-blue-500/10 text-blue-600 border-blue-200';
      case 'llm-response': return 'bg-green-500/10 text-green-600 border-green-200';
      case 'error': return 'bg-red-500/10 text-red-600 border-red-200';
      case 'info': return 'bg-gray-500/10 text-gray-600 border-gray-200';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-200';
    }
  };

  const addManualLog = (type: DebugLog['type'], message: string, data?: any) => {
    const log: DebugLog = {
      id: Date.now().toString() + Math.random(),
      timestamp: new Date().toLocaleTimeString(),
      type,
      message,
      data
    };
    setLogs(prev => [...prev.slice(-49), log]);
  };

  // Expose addLog method to parent components via ref
  React.useImperativeHandle(onLogCapture as any, () => ({
    addLog: addManualLog
  }), []);

  return (
    <Card className="fixed bottom-4 right-4 w-96 max-h-96 z-50 shadow-lg border-2 border-primary/20">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 cursor-pointer bg-muted/50 rounded-t-lg"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">LLM Debug Console</span>
          <Badge variant="secondary" className="text-xs">
            {logs.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          {!isCollapsed && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  copyLogs();
                }}
                className="h-6 w-6 p-0"
              >
                <Copy className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  clearLogs();
                }}
                className="h-6 w-6 p-0"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
          {isCollapsed ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </div>

      {/* Logs Content */}
      {!isCollapsed && (
        <div className="h-80 overflow-y-auto p-3 space-y-2 bg-background text-xs font-mono">
          {logs.length === 0 ? (
            <div className="text-muted-foreground text-center py-8">
              No LLM logs captured yet.<br />
              Try performing a search to see logs.
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="border-l-2 border-muted pl-3 py-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-xs px-1.5 py-0.5 ${getTypeColor(log.type)}`}>
                    {log.type}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {log.timestamp}
                  </span>
                  {log.model && (
                    <Badge variant="outline" className="text-xs">
                      {log.model}
                    </Badge>
                  )}
                  {log.processingTime && (
                    <Badge variant="outline" className="text-xs">
                      {log.processingTime}ms
                    </Badge>
                  )}
                </div>
                <div className="text-foreground whitespace-pre-wrap break-all">
                  {log.message}
                </div>
                {log.data && (
                  <details className="mt-1">
                    <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                      Show data
                    </summary>
                    <pre className="mt-1 p-2 bg-muted/50 rounded text-xs overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      )}

      {/* Auto-scroll toggle */}
      {!isCollapsed && (
        <div className="border-t px-3 py-2 bg-muted/20">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={isAutoScroll}
              onChange={(e) => setIsAutoScroll(e.target.checked)}
              className="rounded"
            />
            Auto-scroll
          </label>
        </div>
      )}
    </Card>
  );
};
